import { useSelector, shallowEqual } from 'react-redux';
import { Link } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import ExternalLinkIcon from '../../ExternalLinkIcon';
import { useRestaurantSettings } from '../../../hooks/useRestaurantSettings';
import { useSiteLogo } from '../../../hooks/useSiteLogo';
import { formatTelHref } from '../../../helpers/phone';
import { formatVatNumber } from '../../../helpers/vat';
import { phoneDeskText, serviceLines } from '../../../helpers/hours';
import './Footer.scss';

const messages = defineMessages({
  login: {
    id: 'Login',
    defaultMessage: 'Connexion',
  },
  logout: {
    id: 'Logout',
    defaultMessage: 'Déconnexion',
  },
  legalLinks: {
    id: 'Legal information',
    defaultMessage: 'Informations légales',
  },
});

const Footer = () => {
  const intl = useIntl();
  const settings = useRestaurantSettings();
  const year = new Date().getFullYear();
  const vatNumber = formatVatNumber(settings.enterprise_number);
  const services = serviceLines(settings.opening_hours);

  const siteLogo = useSiteLogo();
  const token = useSelector(
    (state: any) => state.userSession?.token,
    shallowEqual,
  );

  return (
    <footer className="site-footer">
      <div className="shell foot-grid">
        <div className="foot-brand">
          {siteLogo ? (
            <img
              className="foot-brand-logo"
              src={siteLogo}
              alt="À Ciel Ouvert"
            />
          ) : (
            <span className="foot-brand-name">À ciel ouvert</span>
          )}
          {settings.footer_text && <p>{settings.footer_text}</p>}
          {(settings.facebook_url || settings.instagram_url) && (
            <ul className="foot-social" aria-label="Réseaux sociaux">
              {settings.facebook_url && (
                <li>
                  <a
                    className="external-link"
                    href={settings.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Facebook <ExternalLinkIcon />
                  </a>
                </li>
              )}
              {settings.instagram_url && (
                <li>
                  <a
                    className="external-link"
                    href={settings.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Instagram <ExternalLinkIcon />
                  </a>
                </li>
              )}
            </ul>
          )}
        </div>
        <div className="foot-col">
          <h4>Adresse</h4>
          <ul>
            <li>{settings.address_line1}</li>
            <li>
              {settings.postal_code} {settings.city}, {settings.country}
            </li>
            {settings.email && (
              <li>
                <a href={`mailto:${settings.email}`}>{settings.email}</a>
              </li>
            )}
            {settings.maps_url && (
              <li>
                <a
                  className="external-link"
                  href={settings.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Itinéraire <ExternalLinkIcon />
                </a>
              </li>
            )}
          </ul>
        </div>
        <div className="foot-col">
          <h4>Horaires</h4>
          <ul>
            {services.map((line) => (
              <li key={line}>{line}</li>
            ))}
            <li>
              <UniversalLink href="/#horaires">Tous les horaires</UniversalLink>
            </li>
          </ul>
        </div>
        <div className="foot-col">
          <h4>Réserver</h4>
          <ul>
            <li>
              <UniversalLink href="/#reservation">En ligne</UniversalLink>
            </li>
            {settings.phone_primary && (
              <li>
                <a className="tel" href={formatTelHref(settings.phone_primary)}>
                  {settings.phone_primary}
                </a>
              </li>
            )}
            {settings.phone_secondary && (
              <li>
                <a
                  className="tel"
                  href={formatTelHref(settings.phone_secondary)}
                >
                  {settings.phone_secondary}
                </a>
              </li>
            )}
            {settings.phone_hours && (
              <li className="foot-note">
                {phoneDeskText(settings.phone_hours)}
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className="shell foot-bottom">
        <span>
          © {year} À Ciel Ouvert · {settings.city}
          {vatNumber && <> · TVA {vatNumber}</>}
        </span>
        <span className="foot-sep" aria-hidden="true">
          ·
        </span>
        <nav
          className="foot-legal"
          aria-label={intl.formatMessage(messages.legalLinks)}
        >
          <Link to="/mentions-legales">Mentions légales</Link>
          <span className="foot-sep" aria-hidden="true">
            ·
          </span>
          <Link to="/vie-privee">Vie privée</Link>
          <span className="foot-sep" aria-hidden="true">
            ·
          </span>
          {token ? (
            <Link to="/logout" className="foot-login">
              {intl.formatMessage(messages.logout)}
            </Link>
          ) : (
            <Link to="/login" className="foot-login">
              {intl.formatMessage(messages.login)}
            </Link>
          )}
        </nav>
      </div>
    </footer>
  );
};

export default Footer;
